import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '../App';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Share2, Copy, Users, TrendingUp, Gift, ArrowLeft, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const ReferralPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [stats, setStats] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, listRes] = await Promise.all([
        axios.get(`${API}/referrals/stats`, { headers: { Authorization: `Bearer ${localStorage.getItem('arthik_token')}` } }),
        axios.get(`${API}/referrals/list`, { headers: { Authorization: `Bearer ${localStorage.getItem('arthik_token')}` } })
      ]);
      setStats(statsRes.data);
      setReferrals(listRes.data);
      setLoading(false);
    } catch (e) {
      toast.error('Failed to load referrals');
      setLoading(false);
    }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(stats.referral_code);
    toast.success('Referral code copied!');
  };

  const shareLink = () => {
    const url = `${window.location.origin}?ref=${stats.referral_code}`;
    navigator.clipboard.writeText(url);
    toast.success('Referral link copied!');
  };

  const getStatusBadge = (status) => {
    const styles = {
      registered: { bg: '#FEF3C7', color: '#92400E', label: 'Registered' },
      pending_payout: { bg: '#DBEAFE', color: '#1E3A8A', label: 'Pending Payout' },
      successful: { bg: '#D1FAE5', color: '#065F46', label: 'Successful' }
    };
    const s = styles[status] || styles.registered;
    return <span style={{ padding: '0.25rem 0.75rem', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: '700', backgroundColor: s.bg, color: s.color }}>{s.label}</span>;
  };

  if (loading) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', backgroundColor: '#F8FAFC' }}><div style={{ color: '#FF6B35' }}>Loading...</div></div>;
  }

  return (
    <div style={{ backgroundColor: '#F8FAFC', minHeight: '100vh' }}>
      <Navbar />

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '4rem 2rem' }}>
        <button onClick={() => navigate('/')} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'none', border: 'none', color: '#FF6B35', cursor: 'pointer', marginBottom: '2rem', fontWeight: '600' }} data-testid="back-to-home-button">
          <ArrowLeft size={20} /> Back to Home
        </button>

        <div style={{ marginBottom: '3rem' }}>
          <h1 style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', marginBottom: '0.5rem', color: '#0F172A' }}>Refer & Earn</h1>
          <p style={{ fontSize: '1.125rem', color: '#475569' }}>Earn ₹500 per successful referral, ₹1,000 after 5 referrals</p>
        </div>

        {/* Referral Code Card */}
        <div className="calculator-section" style={{ marginBottom: '2rem', background: 'linear-gradient(135deg, #FF6B35 0%, #E55100 100%)', color: 'white' }} data-testid="referral-code-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <div style={{ fontSize: '0.875rem', opacity: 0.9, marginBottom: '0.5rem' }}>Your Referral Code</div>
              <div style={{ fontSize: '2.5rem', fontWeight: '800', fontFamily: 'JetBrains Mono', letterSpacing: '0.1em' }} data-testid="referral-code-display">
                {stats?.referral_code}
              </div>
              <div style={{ fontSize: '0.875rem', opacity: 0.85, marginTop: '0.5rem' }}>{stats?.current_tier}</div>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <button onClick={copyCode} style={{ background: 'rgba(255,255,255,0.2)', color: 'white', borderRadius: '9999px', padding: '0.75rem 1.5rem', border: '1px solid rgba(255,255,255,0.3)', cursor: 'pointer', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem' }} data-testid="copy-code-button">
                <Copy size={16} /> Copy Code
              </button>
              <button onClick={shareLink} style={{ background: 'white', color: '#FF6B35', borderRadius: '9999px', padding: '0.75rem 1.5rem', border: 'none', cursor: 'pointer', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '0.5rem' }} data-testid="share-link-button">
                <Share2 size={16} /> Share Link
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.5rem', marginBottom: '3rem' }}>
          <div className="stat-card" data-testid="stat-total-referrals">
            <Users size={28} style={{ color: '#FF6B35', marginBottom: '0.75rem' }} />
            <div style={{ fontSize: '0.875rem', color: '#475569', marginBottom: '0.5rem' }}>Total Referrals</div>
            <div style={{ fontSize: '2rem', fontWeight: '700', color: '#0F172A' }}>{stats?.total_referrals}</div>
          </div>
          <div className="stat-card" data-testid="stat-successful-referrals">
            <CheckCircle size={28} style={{ color: '#FF6B35', marginBottom: '0.75rem' }} />
            <div style={{ fontSize: '0.875rem', color: '#475569', marginBottom: '0.5rem' }}>Successful</div>
            <div style={{ fontSize: '2rem', fontWeight: '700', color: '#065F46' }}>{stats?.successful_referrals}</div>
          </div>
          <div className="stat-card" data-testid="stat-total-earnings">
            <Gift size={28} style={{ color: '#004D7A', marginBottom: '0.75rem' }} />
            <div style={{ fontSize: '0.875rem', color: '#475569', marginBottom: '0.5rem' }}>Total Earnings</div>
            <div style={{ fontSize: '2rem', fontWeight: '700', color: '#FF6B35' }}>₹{stats?.total_earnings?.toLocaleString()}</div>
          </div>
          <div className="stat-card" data-testid="stat-pending-earnings">
            <TrendingUp size={28} style={{ color: '#F59E0B', marginBottom: '0.75rem' }} />
            <div style={{ fontSize: '0.875rem', color: '#475569', marginBottom: '0.5rem' }}>Pending</div>
            <div style={{ fontSize: '2rem', fontWeight: '700', color: '#92400E' }}>₹{stats?.pending_earnings?.toLocaleString()}</div>
          </div>
        </div>

        {stats?.next_tier_at && (
          <div className="calculator-section" style={{ marginBottom: '2rem', backgroundColor: '#FFF7ED', border: '2px dashed #FFB627' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <TrendingUp size={32} style={{ color: '#FFB627' }} />
              <div>
                <div style={{ fontWeight: '700', color: '#0F172A', marginBottom: '0.25rem' }}>
                  {stats.next_tier_at} more referrals to unlock Tier 2!
                </div>
                <div style={{ color: '#475569', fontSize: '0.875rem' }}>
                  Get ₹1,000 per referral after 5 successful referrals
                </div>
              </div>
            </div>
          </div>
        )}

        {/* How it works */}
        <div className="calculator-section" style={{ marginBottom: '2rem' }}>
          <h2 style={{ fontSize: '1.5rem', marginBottom: '2rem', color: '#0F172A' }}>How it Works</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '2rem' }}>
            {[
              { num: '1', title: 'Share Your Code', desc: 'Send your unique referral code to friends & family' },
              { num: '2', title: 'They Sign Up', desc: 'Friend uses your code while creating account' },
              { num: '3', title: 'They Apply', desc: 'Friend applies for a loan or service' },
              { num: '4', title: 'You Earn', desc: 'Get ₹500 once their application is approved' }
            ].map(step => (
              <div key={step.num} style={{ textAlign: 'center' }}>
                <div style={{ width: '50px', height: '50px', borderRadius: '50%', backgroundColor: '#FF6B35', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '1.25rem', margin: '0 auto 1rem', fontFamily: 'Outfit' }}>
                  {step.num}
                </div>
                <h4 style={{ fontWeight: '700', color: '#0F172A', marginBottom: '0.5rem' }}>{step.title}</h4>
                <p style={{ color: '#475569', fontSize: '0.875rem' }}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Referrals List */}
        <div className="calculator-section">
          <h2 style={{ fontSize: '1.5rem', marginBottom: '1.5rem', color: '#0F172A' }}>Your Referrals</h2>
          {referrals.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#475569' }}>
              <Users size={48} style={{ color: '#E2E8F0', margin: '0 auto 1rem' }} />
              <p>No referrals yet. Share your code to start earning!</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '500px' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #E2E8F0' }}>
                    <th style={{ padding: '1rem', textAlign: 'left', color: '#0F172A' }}>Name</th>
                    <th style={{ padding: '1rem', textAlign: 'left', color: '#0F172A' }}>Email</th>
                    <th style={{ padding: '1rem', textAlign: 'left', color: '#0F172A' }}>Status</th>
                    <th style={{ padding: '1rem', textAlign: 'right', color: '#0F172A' }}>Earnings</th>
                  </tr>
                </thead>
                <tbody>
                  {referrals.map(r => (
                    <tr key={r.id} style={{ borderBottom: '1px solid #E2E8F0' }} data-testid={`referral-row-${r.id}`}>
                      <td style={{ padding: '1rem', fontWeight: '600' }}>{r.referee_name}</td>
                      <td style={{ padding: '1rem', color: '#475569' }}>{r.referee_email}</td>
                      <td style={{ padding: '1rem' }}>{getStatusBadge(r.status)}</td>
                      <td style={{ padding: '1rem', textAlign: 'right', fontWeight: '700', color: r.earnings > 0 ? '#FF6B35' : '#475569' }}>
                        ₹{r.earnings?.toLocaleString() || 0}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReferralPage;
